import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { Search, CheckCircle2, Undo2, ArrowLeft, Users, UserCheck } from "lucide-react";
import api from "../lib/api";
import SEO from "../components/SEO";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

/**
 * EventCheckIn — mobile-optimized RSVP check-in page for event-day use.
 * Admin/board scans QR or types name → taps name → checked-in.
 * Idempotent + offline-tolerant: if a name doesn't show up, you can refresh.
 */
const EventCheckIn = () => {
    const { eventId } = useParams();
    const { user } = useAuth();
    const [event, setEvent] = useState(null);
    const [query, setQuery] = useState("");
    const [rsvps, setRsvps] = useState([]);
    const [stats, setStats] = useState({ rsvp_count: 0, party_total: 0, checked_count: 0, checked_party: 0 });
    const [loading, setLoading] = useState(true);
    const [busyId, setBusyId] = useState(null);

    const load = useCallback(async () => {
        try {
            const [search, sx] = await Promise.all([
                api.get(`/events/${eventId}/checkin/search`, { params: { q: query } }),
                api.get(`/events/${eventId}/checkin-stats`),
            ]);
            setRsvps(search.data || []);
            setStats(sx.data);
        } catch (e) {
            // ignore; show toast only if we had nothing
        } finally {
            setLoading(false);
        }
    }, [eventId, query]);

    useEffect(() => {
        // Fetch the event once for header
        api.get(`/events/public`).then((r) => {
            const found = (r.data || []).find((e) => e.id === eventId);
            if (found) setEvent(found);
        }).catch(() => {});
    }, [eventId]);

    useEffect(() => {
        const t = setTimeout(load, 250); // debounce search
        return () => clearTimeout(t);
    }, [load]);

    const checkIn = async (rsvp) => {
        setBusyId(rsvp.id);
        try {
            const { data } = await api.post(`/events/${eventId}/rsvps/${rsvp.id}/checkin`);
            if (data.already) {
                toast.info(`${rsvp.name} was already checked in`);
            } else {
                toast.success(`Checked in: ${rsvp.name} (party of ${rsvp.party_size})`);
            }
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Check-in failed");
        } finally {
            setBusyId(null);
        }
    };

    const undoCheckIn = async (rsvp) => {
        setBusyId(rsvp.id);
        try {
            await api.post(`/events/${eventId}/rsvps/${rsvp.id}/uncheckin`);
            toast.success(`Undid check-in for ${rsvp.name}`);
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Undo failed");
        } finally {
            setBusyId(null);
        }
    };

    if (!user || (user.role !== "admin" && user.role !== "board")) {
        return (
            <div className="max-w-md mx-auto px-6 py-20 text-center" data-testid="checkin-no-auth">
                <p className="text-[#4A5568] mb-4">Admin or board sign-in required.</p>
                <Link to="/login" className="text-[#E53935] underline">Sign in</Link>
            </div>
        );
    }

    const pct = stats.party_total > 0 ? Math.round((stats.checked_party / stats.party_total) * 100) : 0;

    return (
        <div className="min-h-[calc(100vh-160px)] bg-[#FDFBF7]" data-testid="event-checkin-page">
            <SEO title="Event Check-in" path={`/admin/events/${eventId}/checkin`} noIndex />

            {/* Sticky header — name, stats, search */}
            <div className="sticky top-20 z-30 bg-[#FDFBF7] border-b border-black/5">
                <div className="max-w-2xl mx-auto px-4 py-4">
                    <Link to="/admin" className="inline-flex items-center gap-1.5 text-xs text-[#4A5568] hover:text-[#E53935] mb-3" data-testid="checkin-back-link">
                        <ArrowLeft className="w-3.5 h-3.5" strokeWidth={2} />
                        Back to admin
                    </Link>
                    <h1 className="font-heading text-2xl text-[#1C252E] font-medium leading-tight">
                        {event?.title || "Event check-in"}
                    </h1>

                    {/* Stats */}
                    <div className="grid grid-cols-3 gap-2 mt-4" data-testid="checkin-stats">
                        <div className="bg-white rounded-xl px-3 py-2 border border-black/5">
                            <p className="text-[10px] uppercase tracking-wider text-[#4A5568] font-bold">Checked in</p>
                            <p className="font-heading text-xl text-[#E53935] font-medium leading-none mt-1" data-testid="checkin-stat-checked">
                                {stats.checked_party}<span className="text-sm text-[#4A5568]">/{stats.party_total}</span>
                            </p>
                        </div>
                        <div className="bg-white rounded-xl px-3 py-2 border border-black/5">
                            <p className="text-[10px] uppercase tracking-wider text-[#4A5568] font-bold">RSVPs</p>
                            <p className="font-heading text-xl text-[#1C252E] font-medium leading-none mt-1">{stats.rsvp_count}</p>
                        </div>
                        <div className="bg-white rounded-xl px-3 py-2 border border-black/5">
                            <p className="text-[10px] uppercase tracking-wider text-[#4A5568] font-bold">Filled</p>
                            <p className="font-heading text-xl text-[#1C252E] font-medium leading-none mt-1">{pct}%</p>
                        </div>
                    </div>

                    {/* Search */}
                    <div className="relative mt-4">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#4A5568]" strokeWidth={1.5} />
                        <input
                            type="text"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Type name or email…"
                            aria-label="Search RSVPs by name or email"
                            data-testid="checkin-search-input"
                            autoFocus
                            className="w-full rounded-2xl border border-black/10 pl-11 pr-4 py-3 bg-white focus:outline-none focus:ring-2 focus:ring-[#E53935]/40 focus:border-[#E53935] transition-all text-base"
                        />
                    </div>
                </div>
            </div>

            {/* RSVP list */}
            <div className="max-w-2xl mx-auto px-4 py-4 space-y-2" data-testid="checkin-rsvp-list">
                {loading ? (
                    <div className="text-center text-[#4A5568] py-10" role="status">Loading…</div>
                ) : rsvps.length === 0 ? (
                    <div className="text-center text-[#4A5568] py-10" data-testid="checkin-empty">
                        {query ? "No matches" : "No RSVPs yet"}
                    </div>
                ) : (
                    rsvps.map((r) => {
                        const isCheckedIn = !!r.checked_in_at;
                        return (
                            <div
                                key={r.id}
                                data-testid={`checkin-rsvp-${r.id}`}
                                className={`flex items-center justify-between gap-3 rounded-2xl border p-4 ${
                                    isCheckedIn ? "bg-[#F4F1EA] border-[#E53935]/30" : "bg-white border-black/5"
                                }`}
                            >
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                        {isCheckedIn && <CheckCircle2 className="w-4 h-4 text-[#E53935] flex-shrink-0" strokeWidth={2} fill="#F2D8D5" />}
                                        <p className="font-medium text-[#1C252E] truncate">{r.name}</p>
                                    </div>
                                    <p className="text-xs text-[#4A5568] truncate">
                                        {r.email} · <Users className="inline w-3 h-3 -mt-0.5" strokeWidth={2} /> party of {r.party_size}
                                        {isCheckedIn && r.checked_in_at && (
                                            <span className="ml-1 text-[#E53935]">
                                                · checked in {new Date(r.checked_in_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}
                                            </span>
                                        )}
                                    </p>
                                </div>
                                {isCheckedIn ? (
                                    <button
                                        type="button"
                                        onClick={() => undoCheckIn(r)}
                                        disabled={busyId === r.id}
                                        data-testid={`checkin-undo-${r.id}`}
                                        className="flex-shrink-0 inline-flex items-center gap-1.5 text-xs font-medium text-[#4A5568] hover:text-[#E53935] rounded-full px-3 py-1.5 border border-black/10 transition-all disabled:opacity-50"
                                        aria-label={`Undo check-in for ${r.name}`}
                                    >
                                        <Undo2 className="w-3.5 h-3.5" strokeWidth={2} />
                                        Undo
                                    </button>
                                ) : (
                                    <button
                                        type="button"
                                        onClick={() => checkIn(r)}
                                        disabled={busyId === r.id}
                                        data-testid={`checkin-confirm-${r.id}`}
                                        className="flex-shrink-0 inline-flex items-center gap-1.5 text-sm font-medium bg-[#E53935] text-white hover:bg-[#D32F2F] rounded-full px-4 py-2 transition-all disabled:opacity-60"
                                        aria-label={`Check in ${r.name}`}
                                    >
                                        <UserCheck className="w-4 h-4" strokeWidth={2} />
                                        Check in
                                    </button>
                                )}
                            </div>
                        );
                    })
                )}
            </div>
        </div>
    );
};

export default EventCheckIn;
